import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Card } from 'react-daisyui'
import { session } from '../../utils/AuthSession'
import { API } from '../../utils/api'
import { Api } from '../../utils/apiServer'
import { getGuestVisitorId, getMemberActivityKey, shouldRecordActivityVisit } from '../../utils/activityVisitGuard'
import DefaultProduct from '../../assets/default-product.jpg'
import DefaultShop from '../../assets/coffee-shop.png'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { AiOutlineInfoCircle, AiOutlineReload } from 'react-icons/ai'
import { StoreContext } from '../../context/StoreContext'
import { savePendingCartAdd, saveProductCatalogContext } from '../../utils/productCatalogContext'

const toNumber = (value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
}

const isResponseSuccess = (response) => {
    if (response?.status === true) return true
    if (typeof response?.success === 'string' && response.success.length > 0) return true
    return false
}

const INTERNAL_MARKETPLACE_IDENTIFIER = 'INTERNAL_MARKETPLACE'

const formatRupiah = (value) => {
    return `Rp ${Intl.NumberFormat('en-ID').format(toNumber(value))}`
}

const toCoordinateNumber = (value) => {
    if (value === null || value === undefined) return null
    if (typeof value === 'string' && value.trim() === '') return null
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

const isValidCoordinatePair = (latitude, longitude) => {
    return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
}

const parseCoordinateString = (coordinateValue) => {
    if (typeof coordinateValue !== 'string') return null

    const [latitudeText, longitudeText] = coordinateValue.split(',').map((value) => value.trim())
    if (!latitudeText || !longitudeText) return null

    const latitude = toCoordinateNumber(latitudeText)
    const longitude = toCoordinateNumber(longitudeText)
    if (latitude === null || longitude === null) return null
    if (!isValidCoordinatePair(latitude, longitude)) return null

    return { lat: latitude, lng: longitude }
}

const resolveCoordinatePair = (source) => {
    if (!source || typeof source !== 'object') return null

    const nestedStore = source?.store && typeof source.store === 'object' ? source.store : null
    const coordinateStringCandidates = [
        source?.coordinate,
        source?.store_coordinate,
        source?.storeCoordinate,
        source?.location_coordinate,
        source?.lat_long,
        source?.latlng,
        nestedStore?.coordinate,
        nestedStore?.store_coordinate,
        nestedStore?.storeCoordinate,
    ]

    for (const coordinateValue of coordinateStringCandidates) {
        const parsed = parseCoordinateString(coordinateValue)
        if (parsed) return parsed
    }

    const latitudeCandidates = [
        source?.latitude,
        source?.lat,
        source?.store_latitude,
        source?.store_lat,
        source?.coordinate_lat,
        source?.address_latitude,
        nestedStore?.latitude,
        nestedStore?.lat,
        nestedStore?.store_latitude,
        nestedStore?.store_lat,
        nestedStore?.coordinate_lat,
    ]
    const longitudeCandidates = [
        source?.longitude,
        source?.lng,
        source?.long,
        source?.store_longitude,
        source?.store_lng,
        source?.coordinate_lng,
        source?.address_longitude,
        nestedStore?.longitude,
        nestedStore?.lng,
        nestedStore?.long,
        nestedStore?.store_longitude,
        nestedStore?.store_lng,
        nestedStore?.coordinate_lng,
    ]

    const latitude = latitudeCandidates
        .map((value) => toCoordinateNumber(value))
        .find((value) => value !== null)
    const longitude = longitudeCandidates
        .map((value) => toCoordinateNumber(value))
        .find((value) => value !== null)

    if (latitude === undefined || latitude === null) return null
    if (longitude === undefined || longitude === null) return null
    if (!isValidCoordinatePair(latitude, longitude)) return null

    return { lat: latitude, lng: longitude }
}

const formatCoordinateForApi = (pair) => {
    if (!pair) return ''
    if (!Number.isFinite(pair.lat) || !Number.isFinite(pair.lng)) return ''
    return `${pair.lat.toFixed(6)},${pair.lng.toFixed(6)}`
}

const getStoreKey = (store) => `${store?.storeSlug || store?.storeName || ''}`

const isDefaultAddress = (address) => {
    const value = address?.address_default
    return value === '1' || value === 1 || value === true || value === 'true'
}

const toNullableNumber = (value) => {
    if (value === null || value === undefined) return null
    if (typeof value === 'string' && value.trim() === '') return null
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

const extractDistanceKm = (response) => {
    const candidates = [
        response?.distance_km?.Instant,
        response?.distance_km?.instant,
        response?.data?.Instant?.distance,
        response?.data?.instant?.distance,
    ]

    for (const value of candidates) {
        const parsed = toNullableNumber(value)
        if (parsed !== null) return parsed
    }

    return null
}

const formatDistanceKm = (value) => {
    const distance = toNullableNumber(value)
    if (distance === null) return null
    return Number.isInteger(distance) ? `${distance}` : distance.toFixed(1)
}

const pickMenuForDetail = (menus) => {
    if (!Array.isArray(menus) || menus.length === 0) return null
    const withStock = menus.find((menu) => toNumber(menu?.menu_current_quantity) > 0)
    return withStock || menus[0]
}

const resolveMenuPricing = (menu) => {
    const menuDiscountPrice = toNumber(menu?.menu_discount_price)
    const menuRegularPrice = toNumber(menu?.menu_regular_price)
    const menuPrice = menuDiscountPrice > 0 ? menuDiscountPrice : menuRegularPrice

    const platforms = Array.isArray(menu?.menu_platform) ? menu.menu_platform : []
    const internalMarketplace = platforms.find(
        (platform) =>
            platform?.ms_merchant_payment?.ms_merchant_payment_identifier ===
            INTERNAL_MARKETPLACE_IDENTIFIER
    )

    if (internalMarketplace) {
        const platformDiscountPrice = toNumber(internalMarketplace?.menu_platform_discount_price)
        const platformRegularPrice = toNumber(internalMarketplace?.menu_platform_regular_price)
        const platformPrice = platformDiscountPrice > 0 ? platformDiscountPrice : platformRegularPrice

        if (platformPrice > 0) {
            return {
                finalPrice: platformPrice,
                priceSource: INTERNAL_MARKETPLACE_IDENTIFIER,
            }
        }
    }

    return {
        finalPrice: menuPrice,
        priceSource: 'MENU',
    }
}

const mergeStoreStocks = (rows) => {
    const keyed = new Map()
    const sourcePriority = {
        [INTERNAL_MARKETPLACE_IDENTIFIER]: 2,
        MENU: 1,
        UNKNOWN: 0,
    }

    const pickPreferredPrice = (previous, current) => {
        const previousPriority = sourcePriority[previous?.priceSource] ?? sourcePriority.UNKNOWN
        const currentPriority = sourcePriority[current?.priceSource] ?? sourcePriority.UNKNOWN
        const previousPrice = toNumber(previous?.finalPrice)
        const currentPrice = toNumber(current?.finalPrice)

        if (currentPriority > previousPriority) {
            return {
                finalPrice: currentPrice,
                priceSource: current?.priceSource || 'UNKNOWN',
            }
        }

        if (currentPriority < previousPriority) {
            return {
                finalPrice: previousPrice,
                priceSource: previous?.priceSource || 'UNKNOWN',
            }
        }

        if (previousPrice <= 0 && currentPrice > 0) {
            return {
                finalPrice: currentPrice,
                priceSource: current?.priceSource || 'UNKNOWN',
            }
        }

        if (currentPrice <= 0) {
            return {
                finalPrice: previousPrice,
                priceSource: previous?.priceSource || 'UNKNOWN',
            }
        }

        return {
            finalPrice: currentPrice < previousPrice ? currentPrice : previousPrice,
            priceSource: previous?.priceSource || current?.priceSource || 'UNKNOWN',
        }
    }

    rows.forEach((row) => {
        const safeName = row?.storeName || 'Toko tanpa nama'
        const keyBase = row?.storeSlug || safeName.toLowerCase()
        const key = `${keyBase}`

        if (!keyed.has(key)) {
            keyed.set(key, {
                storeSlug: row?.storeSlug || '',
                storeName: safeName,
                location: row?.location || '',
                coordinate: row?.coordinate || '',
                stock: Math.max(0, toNumber(row?.stock)),
                finalPrice: Math.max(0, toNumber(row?.finalPrice)),
                priceSource: row?.priceSource || 'UNKNOWN',
            })
            return
        }

        const previous = keyed.get(key)
        const preferredPrice = pickPreferredPrice(previous, row)
        keyed.set(key, {
            ...previous,
            location: previous.location || row?.location || '',
            coordinate: previous.coordinate || row?.coordinate || '',
            stock: previous.stock + Math.max(0, toNumber(row?.stock)),
            finalPrice: preferredPrice.finalPrice,
            priceSource: preferredPrice.priceSource,
        })
    })

    return Array.from(keyed.values()).sort((a, b) => {
        if (b.stock !== a.stock) return b.stock - a.stock
        return a.storeName.localeCompare(b.storeName)
    })
}

const normalizeStoreStocks = (rawData) => {
    if (!rawData || typeof rawData !== 'object') return []

    const detailSource = rawData?.item && typeof rawData.item === 'object' ? rawData.item : rawData
    const menus = Array.isArray(detailSource?.menu)
        ? detailSource.menu
        : Array.isArray(rawData?.menu)
        ? rawData.menu
        : []

    if (menus.length > 0) {
        const mapped = menus
            .map((menu) => {
                const pricing = resolveMenuPricing(menu)
                const storeCoordinate =
                    resolveCoordinatePair(menu?.store || menu) || resolveCoordinatePair(menu)
                return {
                    storeSlug: menu?.store_slug || menu?.store?.store_slug || '',
                    storeName: menu?.store_name || menu?.store?.store_name || 'Toko tanpa nama',
                    location: menu?.store?.store_address || menu?.store_address || '',
                    coordinate: formatCoordinateForApi(storeCoordinate),
                    stock: toNumber(menu?.menu_current_quantity ?? menu?.stock ?? menu?.qty ?? 0),
                    finalPrice: pricing.finalPrice,
                    priceSource: pricing.priceSource,
                }
            })
            .filter((store) => store.storeSlug || store.storeName)

        return mergeStoreStocks(mapped)
    }

    const directSources = [
        detailSource?.stores,
        detailSource?.store_stocks,
        detailSource?.stock_by_store,
        rawData?.stores,
        rawData?.store_stocks,
        rawData?.stock_by_store,
    ]

    const directList = directSources.find((source) => Array.isArray(source)) || []
    if (directList.length > 0) {
        const mapped = directList
            .map((entry) => {
                const entryPricing = resolveMenuPricing(entry)
                const entryCoordinate =
                    resolveCoordinatePair(entry?.store || entry) || resolveCoordinatePair(entry)
                return {
                    storeSlug:
                        entry?.store_slug ||
                        entry?.store?.store_slug ||
                        entry?.slug ||
                        '',
                    storeName:
                        entry?.store_name ||
                        entry?.store?.store_name ||
                        entry?.name ||
                        'Toko tanpa nama',
                    location:
                        entry?.store_address ||
                        entry?.store?.store_address ||
                        entry?.location ||
                        '',
                    coordinate: formatCoordinateForApi(entryCoordinate),
                    stock: toNumber(
                        entry?.stock ??
                            entry?.total_stock ??
                            entry?.qty ??
                            entry?.menu_current_quantity ??
                            0
                    ),
                    finalPrice: entryPricing.finalPrice,
                    priceSource: entryPricing.priceSource,
                }
            })
            .filter((store) => store.storeSlug || store.storeName)

        return mergeStoreStocks(mapped)
    }

    const fallbackStore = detailSource?.store || rawData?.store
    if (fallbackStore && typeof fallbackStore === 'object') {
        const fallbackCoordinate = resolveCoordinatePair(fallbackStore)
        return [
            {
                storeSlug: fallbackStore?.store_slug || '',
                storeName: fallbackStore?.store_name || 'Toko tanpa nama',
                location: fallbackStore?.store_address || '',
                coordinate: formatCoordinateForApi(fallbackCoordinate),
                stock: toNumber(
                    detailSource?.menu_current_quantity ??
                        rawData?.menu_current_quantity ??
                        0
                ),
                finalPrice: 0,
                priceSource: 'UNKNOWN',
            },
        ]
    }

    return []
}

const normalizeDetailData = (rawData) => {
    if (!rawData || typeof rawData !== 'object') return null

    const detailSource = rawData?.item && typeof rawData.item === 'object' ? rawData.item : rawData
    const menuFromList = pickMenuForDetail(detailSource?.menu || rawData?.menu)
    const menuFromObject = detailSource?.menu && !Array.isArray(detailSource.menu) ? detailSource.menu : null
    const menu = menuFromList || menuFromObject || null

    const images = Array.isArray(detailSource?.item_image)
        ? detailSource.item_image
        : Array.isArray(rawData?.item_image)
        ? rawData.item_image
        : []

    const itemRegularPrice = toNumber(
        detailSource?.item_regular_price ??
            rawData?.item_regular_price ??
            menu?.menu_regular_price
    )
    const itemDiscountPrice = toNumber(
        detailSource?.item_discount_price ??
            rawData?.item_discount_price ??
            menu?.menu_discount_price
    )

    let currentPrice = itemDiscountPrice > 0 ? itemDiscountPrice : itemRegularPrice

    if (currentPrice <= 0) {
        const fallbackCurrentPriceCandidates = [
            detailSource?.current_price,
            rawData?.current_price,
            menu?.current_price,
            menu?.menu_discount_price,
            menu?.menu_regular_price,
        ]

        for (const value of fallbackCurrentPriceCandidates) {
            const price = toNumber(value)
            if (price > 0) {
                currentPrice = price
                break
            }
        }
    }

    return {
        ...detailSource,
        item_id: detailSource?.item_id ?? rawData?.item_id,
        item_slug: detailSource?.item_slug ?? rawData?.item_slug ?? '',
        item_name: detailSource?.item_name ?? rawData?.item_name,
        item_description: detailSource?.item_description ?? rawData?.item_description ?? '',
        item_image: images,
        item_regular_price: toNumber(detailSource?.menu_regular_price ?? rawData?.menu_regular_price ?? menu?.menu_regular_price ?? itemRegularPrice),
        item_discount_price: toNumber(detailSource?.menu_discount_marketplace_price ?? rawData?.menu_discount_marketplace_price ?? menu?.menu_discount_marketplace_price),
		has_marketplace_discount: Boolean(detailSource?.has_marketplace_discount ?? rawData?.has_marketplace_discount ?? menu?.has_marketplace_discount),
        menu_slug: detailSource?.menu_slug ?? rawData?.menu_slug ?? menu?.menu_slug ?? '',
        menu_current_quantity: toNumber(detailSource?.menu_current_quantity ?? rawData?.menu_current_quantity ?? menu?.menu_current_quantity),
        current_price: currentPrice,
        menu_discount_price:
            detailSource?.menu_discount_price ??
            rawData?.menu_discount_price ??
            menu?.menu_discount_price ??
            detailSource?.item_discount_price ??
            null,
        store: detailSource?.store ?? rawData?.store ?? menu?.store ?? null,
    }
}

const LoadingState = () => (
    <div className="flex min-h-[70vh] flex-col items-center justify-center" role="status" aria-live="polite">
        <div className="h-11 w-11 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
        <p className="mt-4 text-sm font-medium text-gray-600">Memuat detail produk...</p>
        <span className="sr-only">Mohon tunggu</span>
    </div>
)

function ProductDetail() {
    const { activeStoreSlug, loading: storeLoading } = useContext(StoreContext)
    const [data, setData] = useState(null)
    const [images, setImages] = useState([])
    const [currentSlide, setCurrentSlide] = useState(0)
    const [reviews, setReviews] = useState([])
    const [reviewSummary, setReviewSummary] = useState({ average: 0, total: 0 })
    const [pageLoading, setPageLoading] = useState(true)
    const [storeState, setStoreState] = useState({
        status: 'loading',
        data: [],
        message: '',
    })
    const [shippingGateState, setShippingGateState] = useState({
        loading: false,
        addressStatus: 'idle',
        maxDistanceKm: null,
        storeDistances: {},
    })
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)
    const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false)
    const [descriptionClampHeight, setDescriptionClampHeight] = useState(null)

    const { item_slug: itemSlugParam } = useParams()
    const location = useLocation()
    const menuIdParam = location.state?.productMenuId || null
    const itemIdParam = location.state?.productItemId || (/^\d+$/.test(`${itemSlugParam || ''}`) ? itemSlugParam : null)
    const navigate = useNavigate()
    const imageSliderRef = useRef(null)
    const descriptionRef = useRef(null)
    const currentSession = session()
    const memberActivityKey = getMemberActivityKey(currentSession)
    const isLoggedIn = Boolean(currentSession?.token || currentSession?.user || currentSession)

    useEffect(() => {
        const itemReference = `${menuIdParam || itemIdParam || itemSlugParam || ''}`.trim()
        if (!itemReference) return

        const recordProductDetailVisit = async () => {
            const visitorId = isLoggedIn ? null : getGuestVisitorId()
            const identityKey = isLoggedIn
                ? `member:${memberActivityKey}`
                : `guest:${visitorId}`

            if (!shouldRecordActivityVisit(`product-detail:${identityKey}:${itemReference}`)) return

            await Api({
                access: isLoggedIn ? 'auth' : 'public',
                method: isLoggedIn ? API.MEMBER_PRODUCT_PAGE_VISIT.type : API.PUBLIC_PRODUCT_PAGE_VISIT.type,
                url: isLoggedIn ? API.MEMBER_PRODUCT_PAGE_VISIT.url : API.PUBLIC_PRODUCT_PAGE_VISIT.url,
                reqBody: {
                    page: `/product/${itemSlugParam}`,
                    item_id: itemIdParam || null,
                    item_slug: itemIdParam ? null : itemSlugParam,
                    visitor_id: visitorId,
                },
            })
        }

        recordProductDetailVisit()
    }, [isLoggedIn, itemIdParam, itemSlugParam, memberActivityKey])

    const sliderImages = useMemo(() => {
        if (!Array.isArray(images) || images.length === 0) return [DefaultProduct]
        return images
    }, [images])

    const safeSlideIndex = Math.min(currentSlide, Math.max(0, sliderImages.length - 1))
    const productRegularPrice = toNumber(data?.item_regular_price)
    const productDiscountPrice = toNumber(data?.item_discount_price)
	const hasProductDiscount = Boolean(data?.has_marketplace_discount) && productDiscountPrice > 0 && productDiscountPrice < productRegularPrice
    const productMainPrice = hasProductDiscount ? productDiscountPrice : productRegularPrice

    const fetchReviews = useCallback(async (itemId) => {
        if (!itemId) return

        const params = {
            access: 'public',
            method: API.PUBLIC_PRODUCT_REVIEW.type,
            url: API.PUBLIC_PRODUCT_REVIEW.url,
            reqBody: {
                item_id: itemId,
            },
        }

        const response = await Api(params)
        if (!response?.status) {
            setReviews([])
            setReviewSummary({ average: 0, total: 0 })
            return
        }

        setReviews(response.data?.reviews || [])
        setReviewSummary({
            average: Number(response.data?.average || 0),
            total: Number(response.data?.total || 0),
        })
    }, [])

    const loadStoreStocks = useCallback(async (itemId, fallbackSource = null) => {
        setStoreState({ status: 'loading', data: [], message: '' })

        const params = {
            access: 'public',
            method: API.PUBLIC_PRODUCT.type,
            url: API.PUBLIC_PRODUCT.url,
            reqBody: {
                item_id: itemId,
                store_slug: activeStoreSlug,
            },
        }

        const response = await Api(params)

        if (!isResponseSuccess(response)) {
            const fallbackStores = normalizeStoreStocks(fallbackSource)
            if (fallbackStores.length > 0) {
                setStoreState({ status: 'ready', data: fallbackStores, message: '' })
                return
            }

            setStoreState({
                status: 'error',
                data: [],
                message: response?.message || 'Gagal memuat data toko dan stok.',
            })
            return
        }

        const sourceData = Array.isArray(response?.data) ? response.data[0] : response?.data
        const stores = normalizeStoreStocks(sourceData)

        if (stores.length === 0) {
            setStoreState({ status: 'empty', data: [], message: '' })
            return
        }

        setStoreState({ status: 'ready', data: stores, message: '' })
    }, [activeStoreSlug])

    const loadDetail = useCallback(async () => {
        if (!itemSlugParam || `${itemSlugParam}`.trim() === '') {
            navigate('/404')
            return
        }

        // StoreContext restores the persisted choice asynchronously. Do not
        // treat that short loading period as a missing product.
        if (storeLoading || !activeStoreSlug) return

        setPageLoading(true)

        const primaryParams = {
            access: 'public',
            method: API.PUBLIC_PRODUCT.type,
            url: API.PUBLIC_PRODUCT.url,
            reqBody: {
                ...(menuIdParam ? { menu_id: menuIdParam } : (itemIdParam ? { item_id: itemIdParam } : { item_slug: itemSlugParam })),
                store_slug: activeStoreSlug,
            },
        }

        let response = await Api(primaryParams)

        // The URL is the durable SEO identity. Navigation state can be stale
        // after a catalog refresh, so retry the same active-store lookup by
        // menu slug when the carried menu_id has no result.
        if ((menuIdParam || itemIdParam) && (!isResponseSuccess(response) || !Array.isArray(response?.data) || response.data.length === 0)) {
            response = await Api({
                access: 'public',
                method: API.PUBLIC_PRODUCT.type,
                url: API.PUBLIC_PRODUCT.url,
                reqBody: {
                    item_slug: itemSlugParam,
                    store_slug: activeStoreSlug,
                },
            })
        }

        if (!isResponseSuccess(response)) {
            navigate('/404')
            return
        }

        const sourceData = Array.isArray(response?.data) ? response.data[0] : response?.data
        const normalizedData = normalizeDetailData(sourceData)

        if (!normalizedData) {
            navigate('/404')
            return
        }

        setData(normalizedData)
        setImages(normalizedData.item_image || [])
        setCurrentSlide(0)

        if (normalizedData?.review_summary) {
            setReviewSummary({
                average: Number(normalizedData.review_summary.average || 0),
                total: Number(normalizedData.review_summary.total || 0),
            })
        }

        await Promise.all([
            fetchReviews(normalizedData.item_id),
            loadStoreStocks(normalizedData.item_id, sourceData),
        ])
        setPageLoading(false)
    }, [activeStoreSlug, fetchReviews, itemIdParam, itemSlugParam, loadStoreStocks, menuIdParam, navigate, storeLoading])

    useEffect(() => {
        loadDetail()
    }, [loadDetail])

    useEffect(() => {
        setCurrentSlide(0)
        if (imageSliderRef.current) {
            imageSliderRef.current.scrollTo({ left: 0, behavior: 'auto' })
        }
    }, [sliderImages.length])

    const measureDescriptionOverflow = useCallback(() => {
        const element = descriptionRef.current
        if (!element) return

        const computedStyle = window.getComputedStyle(element)
        const rawLineHeight = parseFloat(computedStyle.lineHeight)
        const rawFontSize = parseFloat(computedStyle.fontSize)
        const effectiveLineHeight = Number.isFinite(rawLineHeight) && rawLineHeight > 0
            ? rawLineHeight
            : Number.isFinite(rawFontSize) && rawFontSize > 0
            ? rawFontSize * 1.5
            : 24

        const clampHeight = effectiveLineHeight * 10
        setDescriptionClampHeight(clampHeight)

        const isOverflowing = element.scrollHeight > clampHeight + 1
        setIsDescriptionOverflowing(isOverflowing)
    }, [])

    useEffect(() => {
        setIsDescriptionExpanded(false)
        setIsDescriptionOverflowing(false)
        setDescriptionClampHeight(null)
    }, [data?.item_id])

    useEffect(() => {
        if (!data?.item_description) {
            setIsDescriptionOverflowing(false)
            setDescriptionClampHeight(null)
            return
        }

        const frame = requestAnimationFrame(() => {
            measureDescriptionOverflow()
        })

        return () => cancelAnimationFrame(frame)
    }, [data?.item_description, measureDescriptionOverflow])

    useEffect(() => {
        if (!data?.item_description) return

        const handleResize = () => {
            if (isDescriptionExpanded) return
            measureDescriptionOverflow()
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [data?.item_description, isDescriptionExpanded, measureDescriptionOverflow])

    const handleImageSliderScroll = useCallback((event) => {
        const container = event.currentTarget
        if (!container) return
        const slideWidth = container.clientWidth
        if (!slideWidth) return

        const nextIndex = Math.round(container.scrollLeft / slideWidth)
        const boundedIndex = Math.max(0, Math.min(nextIndex, sliderImages.length - 1))

        if (boundedIndex !== currentSlide) {
            setCurrentSlide(boundedIndex)
        }
    }, [currentSlide, sliderImages.length])

    useEffect(() => {
        const stores = storeState.status === 'ready' ? storeState.data : []

        if (!isLoggedIn) {
            setShippingGateState({
                loading: false,
                addressStatus: 'guest',
                maxDistanceKm: null,
                storeDistances: {},
            })
            return
        }

        if (stores.length === 0) {
            setShippingGateState({
                loading: false,
                addressStatus: 'idle',
                maxDistanceKm: null,
                storeDistances: {},
            })
            return
        }

        let cancelled = false

        const loadShippingCoverage = async () => {
            setShippingGateState({
                loading: true,
                addressStatus: 'loading',
                maxDistanceKm: null,
                storeDistances: {},
            })

            const addressResponse = await Api({
                access: 'auth',
                method: API.ADDRESS.type,
                url: API.ADDRESS.url,
                reqBody: {},
            })

            if (cancelled) return

            const addressList = Array.isArray(addressResponse?.data) ? addressResponse.data : []
            const defaultAddress = addressList.find((address) => isDefaultAddress(address))
            const destinationCoordinate = resolveCoordinatePair(defaultAddress)

            if (!defaultAddress || !destinationCoordinate) {
                setShippingGateState({
                    loading: false,
                    addressStatus: 'missing',
                    maxDistanceKm: null,
                    storeDistances: {},
                })
                return
            }

            const initialStoreDistances = {}
            stores.forEach((store) => {
                const key = getStoreKey(store)
                if (!key) return
                const originCoordinate = resolveCoordinatePair(store)
                initialStoreDistances[key] = originCoordinate
                    ? {
                        status: 'loading',
                        distanceKm: null,
                        maxDistanceKm: null,
                        canAdd: false,
                    }
                    : {
                        status: 'unavailable',
                        distanceKm: null,
                        maxDistanceKm: null,
                        canAdd: false,
                    }
            })

            setShippingGateState({
                loading: true,
                addressStatus: 'ready',
                maxDistanceKm: null,
                storeDistances: initialStoreDistances,
            })

            const distanceResults = await Promise.all(
                stores.map(async (store) => {
                    const key = getStoreKey(store)
                    if (!key) return null

                    const originCoordinate = resolveCoordinatePair(store)
                    if (!originCoordinate) {
                        return {
                            key,
                            status: 'unavailable',
                            distanceKm: null,
                            maxDistanceKm: null,
                            canAdd: false,
                        }
                    }

                    const response = await Api({
                        access: 'auth',
                        method: API.GOSEND_SHIPPING_COST.type,
                        url: API.GOSEND_SHIPPING_COST.url,
                        reqBody: {
                            origin: formatCoordinateForApi(originCoordinate),
                            destination: formatCoordinateForApi(destinationCoordinate),
                            paymentType: 3,
                        },
                    })

                    const distanceKm = extractDistanceKm(response)
                    const maxDistanceKm = toNullableNumber(response?.max_distance_km)

                    if (distanceKm === null) {
                        return {
                            key,
                            status: 'error',
                            distanceKm: null,
                            maxDistanceKm,
                            canAdd: false,
                        }
                    }

                    return {
                        key,
                        status: 'ready',
                        distanceKm,
                        maxDistanceKm,
                        canAdd: maxDistanceKm !== null && distanceKm < maxDistanceKm,
                    }
                })
            )

            if (cancelled) return

            const mergedStoreDistances = { ...initialStoreDistances }
            let maxDistanceKmValue = null

            distanceResults.forEach((result) => {
                if (!result?.key) return
                mergedStoreDistances[result.key] = result
                if (result.maxDistanceKm !== null && maxDistanceKmValue === null) {
                    maxDistanceKmValue = result.maxDistanceKm
                }
            })

            setShippingGateState({
                loading: false,
                addressStatus: 'ready',
                maxDistanceKm: maxDistanceKmValue,
                storeDistances: mergedStoreDistances,
            })
        }

        loadShippingCoverage()

        return () => {
            cancelled = true
        }
    }, [isLoggedIn, storeState.data, storeState.status])

    const handleAddOnClick = async () => {
        const authSession = session()
        if (!authSession) {
            const productCatalogContext = location.state?.productCatalogContext || { originPath: '/product', storeSlug: activeStoreSlug, scrollTop: 0 }
            saveProductCatalogContext(productCatalogContext)
            savePendingCartAdd({ menuId: data?.menu_id || menuIdParam, storeSlug: activeStoreSlug, returnPath: '/product' })
            navigate('/auth/login', { state: { productCatalogContext, returnTo: '/product' } })
            return
        }

        if (toNumber(data?.menu_current_quantity) <= 0) {
            toast.error('Stok produk sedang tidak tersedia')
            return
        }

        const params = {
            access: 'auth',
            method: API.ADD_CART.type,
            url: API.ADD_CART.url,
            reqBody: {
				menu_id: data?.menu_id || menuIdParam || null,
                store_slug: activeStoreSlug,
                quantity: 1,
            },
        }

        const response = await Api(params)
        if (response?.status) {
            toast(response.message || 'Produk ditambahkan ke keranjang')
            return
        }

        toast.error(response?.message || 'Gagal menambah ke keranjang')
    }

    const resolveStoreDistanceInfo = useCallback((store) => {
        const key = getStoreKey(store)
        if (!key) return null
        return shippingGateState.storeDistances[key] || null
    }, [shippingGateState.storeDistances])

    const shouldShowAddButton = useCallback((store) => {
        const hasStock = toNumber(store?.stock) > 0
        if (!hasStock) return false

        if (!isLoggedIn) return true
        if (shippingGateState.addressStatus === 'loading') return false
        if (shippingGateState.addressStatus === 'missing' || shippingGateState.addressStatus === 'idle') {
            return true
        }

        const info = resolveStoreDistanceInfo(store)
        return Boolean(info?.canAdd)
    }, [isLoggedIn, resolveStoreDistanceInfo, shippingGateState.addressStatus])

    const renderStoreDistanceText = useCallback((store) => {
        if (!isLoggedIn) return null
        if (shippingGateState.addressStatus !== 'ready') return null

        const info = resolveStoreDistanceInfo(store)
        if (!info || info.status === 'loading') return 'Menghitung jarak...'

        const formattedDistance = formatDistanceKm(info.distanceKm)
        if (info.status === 'ready' && formattedDistance !== null) {
            return `Jarak: ${formattedDistance} km`
        }

        return 'Jarak tidak tersedia'
    }, [isLoggedIn, resolveStoreDistanceInfo, shippingGateState.addressStatus])

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <ToastContainer autoClose={1000} />
            <div className="mx-auto max-w-sm px-2 pt-1 pb-6">
                {pageLoading ? (
                    <LoadingState />
                ) : (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <div className="max-w-full overflow-hidden rounded-xl bg-white shadow-lg">
                                <div
                                    ref={imageSliderRef}
                                    className="flex max-w-full w-full snap-x snap-mandatory overflow-x-auto scroll-smooth scrollbar-hide touch-pan-x"
                                    onScroll={handleImageSliderScroll}
                                >
                                    {sliderImages.map((image, index) => (
                                        <div key={`${image}-${index}`} className="aspect-square max-w-full w-full shrink-0 snap-center bg-gray-50">
                                            <img
                                                className="block h-full max-w-full w-full object-cover"
                                                src={image}
                                                alt={`${data?.item_name || 'Produk'} ${index + 1}`}
                                                onError={(event) => {
                                                    event.target.src = DefaultProduct
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {sliderImages.length > 1 ? (
                                    <div className="flex items-center justify-center gap-1.5 px-3 py-2">
                                        {sliderImages.map((_, index) => (
                                            <span
                                                key={`dot-${index}`}
                                                className={`h-1.5 rounded-full transition-all ${index === safeSlideIndex ? 'w-5 bg-blue-600' : 'w-1.5 bg-gray-300'}`}
                                            />
                                        ))}
                                    </div>
                                ) : null}
                            </div>

                            <Card className="rounded-xl bg-white shadow-lg border-0">
                                <Card.Body className="space-y-3 p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h1 className="text-lg font-semibold leading-tight text-gray-900">{data?.item_name}</h1>
                                            <p className="mt-1 text-xs text-gray-500">/{data?.item_slug || itemSlugParam}</p>
                                        </div>
                                        <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700 whitespace-nowrap">
                                            Stok: {data?.menu_current_quantity || 0}
                                        </span>
                                    </div>

                                    <div className="space-y-1">
                                        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
										{hasProductDiscount ? 'Harga Promo Marketplace' : 'Harga'}
                                        </p>
                                        <p className="text-2xl font-bold text-black">
                                            {formatRupiah(productMainPrice || data?.current_price)}
                                        </p>
                                        {hasProductDiscount ? (
                                            <p className="text-xs text-gray-400 line-through">
                                                {formatRupiah(productRegularPrice)}
                                            </p>
                                        ) : null}
                                    </div>

                                    <div className="border-t pt-3 text-sm text-gray-700 leading-relaxed space-y-2">
                                        <div
                                            ref={descriptionRef}
                                            style={
                                                !isDescriptionExpanded
                                                    ? {
                                                        overflow: 'hidden',
                                                        maxHeight: descriptionClampHeight
                                                            ? `${descriptionClampHeight}px`
                                                            : undefined,
                                                    }
                                                    : undefined
                                            }
                                            dangerouslySetInnerHTML={{ __html: data?.item_description || '' }}
                                        />
                                        {isDescriptionOverflowing && !isDescriptionExpanded ? (
                                            <button
                                                type="button"
                                                className="text-xs font-medium text-blue-600 hover:text-blue-700"
                                                onClick={() => setIsDescriptionExpanded(true)}
                                            >
                                                Lebih kengkap ...
                                            </button>
                                        ) : null}
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-sm w-full border-0 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300"
                                        onClick={handleAddOnClick}
                                        disabled={toNumber(data?.menu_current_quantity) <= 0}
                                    >
                                        {toNumber(data?.menu_current_quantity) > 0 ? '+ Keranjang' : 'Stok habis'}
                                    </button>
                                </Card.Body>
                            </Card>
                        </div>

                        <Card className="hidden rounded-xl bg-white shadow-lg border-0">
                            <Card.Body className="space-y-3 p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-900">Ulasan Produk</h3>
                                        <p className="text-xs text-gray-500">Ringkasan dan daftar review</p>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-2xl font-bold text-yellow-500">{reviewSummary.average.toFixed(1)}</div>
                                        <div className="text-xs text-gray-500">{reviewSummary.total} review</div>
                                    </div>
                                </div>

                                <div className="max-h-72 space-y-3 overflow-auto no-scrollbar">
                                    {reviews.length === 0 ? (
                                        <p className="text-sm text-gray-500">Belum ada review.</p>
                                    ) : (
                                        reviews.map((review, index) => (
                                            <div key={index} className="border-b border-gray-100 pb-3 last:border-b-0">
                                                <div className="flex items-center justify-between text-xs text-gray-500">
                                                    <p className="font-semibold text-gray-700">Rating: {review.rating}/5</p>
                                                    <p>{review.created_at}</p>
                                                </div>
                                                {review.comment ? (
                                                    <p className="mt-1 text-sm leading-relaxed text-gray-600">{review.comment}</p>
                                                ) : null}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ProductDetail
